// src/components/Admin/Dashboard.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "./Layout";
import { useAuth } from "../auth/AuthContext";
import { 
  Sun, Zap, Mail, HardDrive, TrendingUp, ArrowUpRight, 
  Loader, AlertTriangle, Cpu, MessageSquare, Calendar 
} from "lucide-react";
import { 
  fetchAdminEnquiries, 
  fetchAdminProjects 
} from "../Services/userServices";

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalEnquiries: 0,
    pendingEnquiries: 0,
    liveProjects: 0,
    totalCapacity: 0,
  });
  const [recentEnquiries, setRecentEnquiries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // 🚀 Load Dashboard Data from Backend
  useEffect(() => {
    const loadDashboardData = async () => {
      setIsLoading(true);
      setError('');
      
      try {
        const [enquiriesResult, projectsResult] = await Promise.all([
          fetchAdminEnquiries(),
          fetchAdminProjects(),
        ]);

        const enquiries = enquiriesResult.success ? enquiriesResult.data : [];
        const pending = enquiries.filter(item => !item.is_resolved).length;

        const projects = projectsResult.success ? projectsResult.data : [];
        const live = projects.filter(p => p.status === 'Completed' || p.status === 'In Progress').length;
        
        const totalCapacityKW = projects.reduce((acc, p) => {
          if (!p.capacity) return acc;
          const num = parseFloat(p.capacity) || 0;
          return acc + (p.capacity.toUpperCase().includes('MW') ? num * 1000 : num);
        }, 0);

        setStats({
          totalEnquiries: enquiries.length,
          pendingEnquiries: pending,
          liveProjects: live,
          totalCapacity: totalCapacityKW.toFixed(1),
        });
        
        setRecentEnquiries(enquiries.slice(0, 6));

      } catch (err) {
        setError('Failed to load dashboard analytics. Please try again.');
      }
      
      setIsLoading(false);
    };

    loadDashboardData();
  }, []);

  // 📊 Stats Cards Data (Classical style)
  const statsCards = [
    {
      title: "Customer Enquiries",
      value: stats.totalEnquiries,
      icon: Mail,
      link: "/admin/enquiries",
      subLabel: `${stats.pendingEnquiries} awaiting response`,
      accent: "text-emerald-600 dark:text-emerald-400",
      bg: "bg-emerald-500/10 border border-emerald-500/20",
    },
    {
      title: "Pending Responses",
      value: stats.pendingEnquiries,
      icon: MessageSquare,
      link: "/admin/enquiries",
      subLabel: "Requires immediate reply",
      accent: "text-amber-600 dark:text-amber-400",
      bg: "bg-amber-500/10 border border-amber-500/20",
    },
    {
      title: "Active Installations",
      value: stats.liveProjects,
      icon: HardDrive,
      link: "/admin/projects",
      subLabel: "Live & In Progress Projects",
      accent: "text-cyan-600 dark:text-cyan-400",
      bg: "bg-cyan-500/10 border border-cyan-500/20",
    },
    {
      title: "Total kW Deployed",
      value: `${stats.totalCapacity} kW`,
      icon: Zap,
      link: "/admin/projects",
      subLabel: "Clean energy generated",
      accent: "text-yellow-600 dark:text-yellow-400",
      bg: "bg-yellow-500/10 border border-yellow-500/20",
    },
  ];

  return (
    <Layout>
      {/* ⚠️ ERROR BANNER */}
      {error && (
        <div className="mb-6 p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 font-bold text-sm flex items-center gap-3">
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* 📊 CLASSICAL STATS GRID (Compact & Sleek Size) */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {statsCards.map((stat, index) => (
          <Link
            to={stat.link}
            key={index}
            className="group bg-white dark:bg-slate-900/50 rounded-xl p-4 border border-slate-200 dark:border-slate-800 shadow-sm dark:shadow-md transition-all duration-300 hover:shadow-md hover:border-emerald-500/30 dark:hover:border-emerald-500/30 cursor-pointer relative"
          >
            <div className="flex justify-between items-start">
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider truncate">
                  {stat.title}
                </p>
                {isLoading ? (
                  <div className="w-14 h-6 mt-2 rounded bg-slate-100 dark:bg-slate-800 animate-pulse" />
                ) : (
                  <p className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white mt-1 tracking-tight">
                    {stat.value}
                  </p>
                )}
                <p className={`text-[9px] font-bold mt-1.5 ${stat.accent} truncate`}>
                  {stat.subLabel}
                </p>
              </div>
              <div className={`w-9 h-9 ${stat.bg} rounded-lg flex items-center justify-center ${stat.accent} group-hover:scale-110 transition-transform shrink-0`}>
                <stat.icon className="w-4 h-4" />
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* 📞 RECENT ENQUIRIES PANEL (Compact rows & alternate colors) */}
      <div className="bg-white dark:bg-slate-900/40 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm dark:shadow-2xl overflow-hidden transition-colors">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800/80 flex justify-between items-center">
          <div>
            <h3 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider">
              Recent Solar Enquiries
            </h3>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
              Latest messages received from customer contact form
            </p>
          </div>
          <Link
            to="/admin/enquiries"
            className="text-xs text-[#10B981] hover:text-emerald-600 dark:hover:text-emerald-400 font-extrabold transition-colors whitespace-nowrap"
          >
            View All →
          </Link>
        </div>
        
        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <Loader className="w-6 h-6 text-[#10B981] animate-spin" />
          </div>
        ) : recentEnquiries.length === 0 ? (
          <div className="p-10 text-center">
            <Mail className="w-10 h-10 text-slate-400 mx-auto mb-3 opacity-60" />
            <h4 className="text-sm font-bold text-slate-700 dark:text-slate-300">No Enquiries Yet</h4>
            <p className="text-xs text-slate-500 mt-1">New customer messages will appear here in real-time.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              {/* Compact Table Header */}
              <thead className="bg-slate-100 dark:bg-slate-950/80 text-slate-600 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                <tr>
                  <th className="px-6 py-2.5 text-[10px] font-bold uppercase tracking-widest">Customer</th>
                  <th className="px-6 py-2.5 text-[10px] font-bold uppercase tracking-widest">Email</th>
                  <th className="px-6 py-2.5 text-[10px] font-bold uppercase tracking-widest">Subject</th>
                  <th className="px-6 py-2.5 text-[10px] font-bold uppercase tracking-widest">Status</th>
                  <th className="px-6 py-2.5 text-[10px] font-bold uppercase tracking-widest">Received</th>
                  <th className="px-6 py-2.5 text-[10px] font-bold uppercase tracking-widest">Action</th>
                </tr>
              </thead>
              {/* Compact Table Body with Zebra Stripes */}
              <tbody>
                {recentEnquiries.map((item, index) => (
                  <tr
                    key={item.id}
                    // 🌈 ALTERNATE ROW COLORS (White & Slate-50)
                    className={`
                      transition-colors border-b border-slate-100 dark:border-slate-800/60
                      ${index % 2 === 0 
                        ? 'bg-white dark:bg-slate-900/20' 
                        : 'bg-slate-50 dark:bg-slate-900/40'
                      }
                      hover:bg-emerald-50/50 dark:hover:bg-slate-800/40
                    `}
                  >
                    {/* Customer Name */}
                    <td className="px-6 py-2.5 text-sm font-bold text-slate-900 dark:text-white whitespace-nowrap">
                      {item.name}
                    </td>
                    {/* Email (NEW COLUMN) */}
                    <td className="px-6 py-2.5 text-xs text-slate-600 dark:text-slate-300 font-medium">
                      {item.email}
                    </td>
                    {/* Subject */}
                    <td className="px-6 py-2.5 text-xs text-slate-600 dark:text-slate-400 font-medium max-w-xs truncate">
                      {item.subject || 'General Enquiry'}
                    </td>
                    {/* Status Badge */}
                    <td className="px-6 py-2.5">
                      <span
                        className={`px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-widest rounded-full ${
                          item.is_resolved
                            ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20"
                            : "bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20"
                        }`}
                      >
                        {item.is_resolved ? "Resolved" : "Pending"}
                      </span>
                    </td>
                    {/* Received Date */}
                    <td className="px-6 py-2.5 text-[11px] text-slate-500 dark:text-slate-400 font-medium whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3 h-3" />
                        {new Date(item.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      </div>
                    </td>
                    {/* Action */}
                    <td className="px-6 py-2.5">
                      <Link
                        to="/admin/enquiries"
                        className="text-[#10B981] hover:text-emerald-600 dark:hover:text-emerald-400 text-xs font-extrabold transition-colors flex items-center gap-1"
                      >
                        Reply <ArrowUpRight className="w-3 h-3" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;